import { useState, useEffect, useRef } from "react";
import "./AboutPage.css";
import img from "../assets/hero-about.jpeg";
import achive1 from "../assets/unf.png";
import achive2 from "../assets/CCA logo.png";
import achive3 from "../assets/ujjwala-logo.png";
import what from "../assets/What-we-do.jpeg";
import Footer from "./Footer";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { X, ExternalLink } from "lucide-react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import TimelineJourney from "./milestones";
import FlameComponent from "./FlameComponent";
import QuoteCard from "./QuoteCard";
import GlobalOperationsMap from './map';
import SEOHead from "../seo/SEOHead";
import { PAGE_SEO } from "../seo/seoConfig";
import { organizationSchema } from "../seo/structuredData";


const AboutPage = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const swiperRef = useRef(null);

  // Mock header image
  const headerImg =
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80";

  useEffect(() => {
    const img = new Image();
    img.src = headerImg;
    img.onload = () => setImageLoaded(true);
  }, []);

  const achievements = [
    {
      id: 1,
      year: "2024",
      title: "UN Foundation Recognition - #EqualEverywhere Movement",
      link: "https://www.linkedin.com/posts/united-nations-foundation_equaleverywhere-activity-7171575899828531203-pd-T/?utm_source=share&utm_medium=member_ios",
      shortDescription:
        "Aarthi Kanodia featured by United Nations Foundation for International Women's Day 2024",
      fullDescription:
        "Aarthi Kanodia was featured by the United Nations Foundation as part of the global #EqualEverywhere movement ahead of International Women's Day 2024. This prestigious campaign highlights bold champions across the world who are driving progress toward gender equality, even when challenges persist. Her recognition reflects Realflame's commitment to empowering women and promoting gender equality in the clean cooking industry.",
      image: achive1,
      stats: [
        { label: "Global Recognition", value: "UN Foundation" },
        { label: "Campaign Reach", value: "Worldwide" },
        { label: "Focus Area", value: "Gender Equality" },
      ],
    },
    {
      id: 2,
      year: "2024",
      title: "Clean Cooking Alliance - Women Energizing Change Series",
      link: "https://cleancooking.org/news/supporting-women-in-the-clean-cooking-value-chain/",
      shortDescription:
        "Featured in CCA's Women Energizing Change Series for empowering women in clean cooking",
      fullDescription:
        "Aarthi Kanodia was featured in the Women Energizing Change Series by the Clean Cooking Alliance (CCA). In this special article, she shared insights on empowering women throughout the clean cooking value chain and on innovative approaches to educating youth about the benefits of clean cooking. This recognition reflects our dedication to advancing sustainable energy solutions, driving gender equality, and shaping a cleaner, healthier future.",
      image: achive2,
      stats: [
        { label: "Featured Platform", value: "Clean Cooking Alliance" },
        { label: "Series Focus", value: "Women Leadership" },
        { label: "Impact Area", value: "Clean Cooking Education" },
      ],
    },
    {
      id: 3,
      year: "2016",
      title: "Empowering Rural India through PM Ujjwala Yojana",
      link: "https://www.pmuy.gov.in/about.html",
      shortDescription:
        "Partnered with Indian Oil Companies under the Pradhan Mantri Ujjwala Yojana to distribute LPG cookstoves and accessories across rural India.",
      fullDescription:
        "RealFlame, a brand of Pearl Print Pack Pvt. Ltd., collaborated with the Government of India under the Pradhan Mantri Ujjwala Yojana (PMUY) — a flagship initiative by the Ministry of Petroleum & Natural Gas to provide clean cooking fuel to underprivileged households. Through this partnership, RealFlame supplied high-quality LPG cookstoves and accessories to millions of families via India's major oil companies, helping replace traditional biomass fuels with LPG. The initiative improved health outcomes and empowered rural women with cleaner cooking solutions. In 2020, RealFlame extended its impact globally by supporting Kenya's 'Gas Yetu' program..",
      image: achive3,
      stats: [
        { label: "Cookstoves Supplied", value: "16 Million+" },
        { label: "Countries Reached", value: "India & Kenya" },
        { label: "Program", value: "PM Ujjwala Yojana" },
      ],
    },
  ];

  const openPopup = (achievement) => {
    setSelectedAchievement(achievement);
  };

  const closePopup = () => {
    setSelectedAchievement(null);
  };

  return (
    <>
      <SEOHead
        title={PAGE_SEO.about.title}
        description={PAGE_SEO.about.description}
        keywords={PAGE_SEO.about.keywords}
        canonical={PAGE_SEO.about.canonical}
        ogImage={PAGE_SEO.about.ogImage}
        structuredData={[organizationSchema]}
      />
      <div className="about-page">
        <div className="header-image-container">
          <img
            src={img}
            alt="RealFlame PNG stove manufacturing facility"
            className={`header-image ${imageLoaded ? "loaded" : ""}`}
          />
        </div>
        <QuoteCard />

        {/* Who Are We Section */}
        <section className="who-are-we-section">
          <div className="content-container">
            <div className="text-content">
              <h2 className="main-title">
                Who <span className="highlight">We Are</span>
              </h2>
              <p className="section-description">
                We are <span className="highlight">Realflame</span>, a team of
                engineers, designers, and industry experts committed to
                transforming the way the world cooks. Our roots are in India,
                but our impact extends across continents. We believe that safe,
                efficient, and clean cooking is a pressing need. With a robust
                in-house R&D setup and ISO-certified manufacturing facilities,
                we've built a legacy that's a symbol of trust, performance, and
                purpose.
              </p>
            </div>
            <div className="image-content">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
                alt="RealFlame engineering team and R&D experts"
                className="section-image"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="what-we-do-section">
          <div className="content-container">
            <div className="image-content">
              <img
                src={what}
                alt="RealFlame PNG stove assembly line"
                className="section-image"
                loading="lazy"
              />
            </div>
            <div className="text-content">
              <h2 className="main-title">
                What <span className="highlight">We Do</span>
              </h2>
              <p className="section-description">
                Realflame is a pioneering manufacturer of innovative and
                energy-efficient gas stoves and clean cooking appliances.
              </p>
              <p className="section-description">
                We design and craft{" "}
                <span className="highlight">
                  {" "}
                  high-quality gas burners, stoves, and LPG/PNG-compatible
                  solutions{" "}
                </span>
                tailored to the unique needs of both Indian and African markets.
                With a commitment to quality, affordability, and sustainability,
                Realflame is driving the transition to clean cooking by offering
                technologically advanced appliances and products that meet
                international safety and energy standards.
              </p>
            </div>
          </div>
        </section>

        {/* Achievements Section */}
        <section style={{ backgroundColor: "#f8f9fa", padding: "0px" }}>
          <div
            style={{ maxWidth: "1024px", margin: "0 auto", padding: "20px" }}
          >
            <h2
              style={{
                fontSize: "2.5rem",
                fontWeight: "500",
                color: "#000",
                marginBottom: "3rem",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <span>
                Our <span style={{ color: "#f58220" }}>Achievements</span>
              </span>
              <a
                href="https://www.realflame.in/achievements"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#f58220",
                  display: "inline-flex",
                  alignItems: "center",
                  textDecoration: "none",
                  transition: "transform 0.2s ease",
                  marginLeft: "0.5rem",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.transform = "scale(1.1)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
                title="View all achievements"
              ></a>
            </h2>
            <style>{`
              @media (max-width: 768px) {
                section h2 {
                  font-size: 24px !important;
                  font-weight: 500 !important;
                  color: rgb(0, 0, 0) !important;
                  margin-bottom: 24px !important;
                  text-align: center !important;
                }
                
                .swiper {
                  padding: 0 10px !important;
                }
              }
            `}</style>

            {/* Achievements Carousel */}
            <div style={{ position: "relative" }}>
              <Swiper
                ref={swiperRef}
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={20}
                slidesPerView={1}
                centeredSlides={false}
                loop={true}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                }}
                navigation={{
                  nextEl: ".swiper-button-next",
                  prevEl: ".swiper-button-prev",
                }}
                breakpoints={{
                  320: {
                    slidesPerView: 1,
                    spaceBetween: 15,
                  },
                  640: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 25,
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                  },
                }}
              >
                {achievements.map((achievement) => (
                  <SwiperSlide key={achievement.id}>
                    <div
                      onClick={() => openPopup(achievement)}
                      style={{
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.borderColor = "transparent";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";

                        e.currentTarget.style.borderColor = "transparent";
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          height: "200px",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={achievement.image}
                          alt={achievement.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "1rem",
                            right: "1rem",
                            backgroundColor: "#f58220",
                            color: "#fff",
                            padding: "0.5rem 1rem",
                            borderRadius: "20px",
                            fontSize: "0.9rem",
                            fontWeight: "bold",
                          }}
                        >
                          {achievement.year}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "1.5rem",
                          display: "flex",
                          flexDirection: "column",
                          flexGrow: 1,
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "1.25rem",
                            fontWeight: "bold",
                            color: "#000",
                            marginBottom: "0.75rem",
                            lineHeight: "1.3",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "0.5rem",
                          }}
                        >
                          <span style={{ flex: 1 }}>{achievement.title}</span>
                          <a
                            href={achievement.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              color: "#f58220",
                              display: "inline-flex",
                              alignItems: "center",
                              textDecoration: "none",
                              transition: "transform 0.2s ease",
                              flexShrink: 0,
                            }}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.transform = "scale(1.15)")
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                            title="Read more"
                          >
                            <ExternalLink size={20} />
                          </a>
                        </h3>
                        <p
                          style={{
                            fontSize: "1rem",
                            color: "#666",
                            lineHeight: "1.5",
                            margin: 0,
                            marginBottom: "1rem",
                            flexGrow: 1,
                          }}
                        >
                          {achievement.shortDescription}
                        </p>
                        <div
                          style={{
                            padding: "0.5rem 1rem",
                            backgroundColor: "#f58220",
                            color: "#fff",
                            borderRadius: "20px",
                            fontSize: "0.9rem",
                            textAlign: "center",
                            fontWeight: "500",
                          }}
                        >
                          Click to learn more
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Achievement Popup Modal */}
            {selectedAchievement && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                  padding: "1rem",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "16px",
                    maxWidth: "800px",
                    width: "100%",
                    maxHeight: "90vh",
                    overflow: "auto",
                    position: "relative",
                  }}
                >
                  <button
                    onClick={closePopup}
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      backgroundColor: "#f58220",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 10,
                      fontSize: "1.2rem",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e6741d")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f58220")
                    }
                  >
                    <X size={20} />
                  </button>

                  <div
                    style={{
                      position: "relative",
                      height: "130px",
                      overflow: "hidden",
                      borderRadius: "16px 16px 0 0",
                    }}
                  >
                    <img
                      src={selectedAchievement.image}
                      alt={selectedAchievement.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "1rem",
                        left: "1rem",
                        backgroundColor: "#f58220",
                        color: "#fff",
                        padding: "0.75rem 1.5rem",
                        borderRadius: "25px",
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                      }}
                    >
                      {selectedAchievement.year}
                    </div>
                  </div>

                  <div style={{ padding: "2rem" }}>
                    <h2
                      style={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: "#000",
                        marginBottom: "1rem",
                        lineHeight: "1.3",
                        paddingRight: "2rem",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.75rem",
                      }}
                    >
                      <span style={{ flex: 1 }}>
                        {selectedAchievement.title}
                      </span>
                      <a
                        href={selectedAchievement.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#f58220",
                          display: "inline-flex",
                          alignItems: "center",
                          textDecoration: "none",
                          transition: "transform 0.2s ease",
                          flexShrink: 0,
                          marginTop: "0.25rem",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.transform = "scale(1.15)")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                        title="Read full article"
                      >
                        <ExternalLink size={24} />
                      </a>
                    </h2>

                    <p
                      style={{
                        fontSize: "16px",
                        color: "#333",
                        lineHeight: "1.7",
                        marginBottom: "2rem",
                      }}
                    >
                      {selectedAchievement.fullDescription}
                    </p>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(150px, 1fr))",
                        gap: "1rem",
                      }}
                    >
                      {selectedAchievement.stats.map((stat, index) => (
                        <div
                          key={index}
                          style={{
                            textAlign: "center",
                            padding: "1rem",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "12px",
                            border: "2px solid #f58220",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: "bold",
                              color: "#f58220",
                              marginBottom: "0.5rem",
                              lineHeight: "1.2",
                            }}
                          >
                            {stat.value}
                          </div>
                          <div
                            style={{
                              fontSize: "clamp(0.8rem, 2vw, 0.9rem)",
                              color: "#666",
                              fontWeight: "500",
                            }}
                          >
                            {stat.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <style>{`
                    @media (max-width: 768px) {
                      .popup-modal > div {
                        padding: 1.5rem !important;
                      }
                      .popup-modal h2 {
                        font-size: 1.5rem !important;
                      }
                      .popup-modal .year-badge {
                        padding: 0.5rem 1rem !important;
                        font-size: 0.9rem !important;
                      }
                    }
                    
                    @media (max-width: 480px) {
                      .popup-modal > div {
                        padding: 1rem !important;
                      }
                    }
                  `}</style>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Mission Section 
        <section className="mission-section">
          <div className="content-container">
            <div className="image-content">
              <img src={mission} alt="Compass" className="section-image" />
            </div>
            <div className="text-content">
              <h2 className="section-title">
                Our <span className="highlight">Mission</span>
              </h2>
              <p className="section-description">
                To deliver cutting-edge, affordable, and eco-friendly gas stove 
                solutions that combine performance, safety, and affordability—improving 
                quality of life while contributing to a greener planet.
              </p>
            </div>
          </div>
        </section*/}

        {/* Vision Section
        <section className="vision-section">
          <div className="content-container">
            <div className="text-content">
              <h2 className="section-title">
                Our <span className="highlight">Vision</span>
              </h2>
              <p className="section-description">
                To become a global leader in clean cooking technologies, 
                empowering every household with safe, sustainable, and affordable 
                cooking solutions.
              </p>
            </div>
            <div className="image-content">
              <img src={vision} alt="Global vision" className="section-image" />
            </div>
          </div>
        </section>  */}
        <FlameComponent />
      </div>
       <GlobalOperationsMap/>
      <TimelineJourney />
      {/* <TornadoRings/> */}
      <Footer />
    </>
  );
};

export default AboutPage;
